import { Progress, Icon } from "semantic-ui-react";
import { Link } from "react-router-dom";
import { banksFormat } from "../../utils/bankList";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatHmrPeriodLabel } from "../../utils/hmrPeriodLabel";
import { formatMoneyWithCurrency } from "../../utils/formatter";

/** @param {string} period YYYYMM o YYYY-MM */
const getPeriodMonthDateRange = (period) => {
  if (!period || typeof period !== "string") return null;
  let year;
  let monthNum;
  if (period.length === 6 && /^\d{6}$/.test(period)) {
    year = parseInt(period.slice(0, 4), 10);
    monthNum = parseInt(period.slice(4, 6), 10);
  } else if (period.includes("-")) {
    const [y, m] = period.split("-");
    year = parseInt(y, 10);
    monthNum = parseInt(m, 10);
  } else {
    return null;
  }
  if (!Number.isFinite(year) || !Number.isFinite(monthNum) || monthNum < 1 || monthNum > 12) {
    return null;
  }
  const mm = String(monthNum).padStart(2, "0");
  const minDate = `${year}-${mm}-01`;
  const lastDay = new Date(year, monthNum, 0).getDate();
  const maxDate = `${year}-${mm}-${String(lastDay).padStart(2, "0")}`;
  return { minDate, maxDate };
};

const buildConciliacionBancariaSearch = ({ minDate, maxDate }) => {
  const qs = new URLSearchParams({
    type: "ALL",
    page: "1",
    offset: "50",
    isMatch: "false",
    minDate,
    maxDate,
  });
  return qs.toString();
};

const banksTableWidths = {
  bankInfo: 40,
  bankCurrency: 10,
  bankReconciledTransactions: 14,
  bankTotalReceived: 18,
  bankTotalSpend: 18
}

const currenciesSummaryTableWidths = {
  csInfo: 15,
  csReconciledTransactions: 25,
  csTotalReceived: 30,
  csTotalSpend: 30
}

export const HMRRow = ({
  hmr,
  onRequestClose,
}) => {
  const { t, i18n } = useTranslation("header");
  const [contentVisibility, setContentVisibility] = useState(false);

  const periodLabel = formatHmrPeriodLabel(hmr.period, i18n.language);

  const totalTx = Number(hmr.transactionsCount) || 0;
  const reconciledTx = Number(hmr.reconciledTransactionsCount) || 0;
  const unReconciledTransactionsCount = Math.max(0, totalTx - reconciledTx);

  const reconciledTransactionsProgress = totalTx === 0
    ? "100.0"
    : ((reconciledTx / totalTx) * 100).toFixed(1);

  const onClickHMRRow = () => {
    setContentVisibility(!contentVisibility);
  }

  const dateRange = getPeriodMonthDateRange(hmr.period);
  const conciliacionTo = dateRange
    ? { pathname: "/conciliacion", search: `?${buildConciliacionBancariaSearch(dateRange)}` }
    : null;

  return (
    <div className="hmr-month-card">
      <div
        className="hmr-month-card__head"
        tabIndex={0}
        onClick={onClickHMRRow}
        onKeyDown={(e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          if (e.target instanceof HTMLElement && e.target.closest("a")) return;
          e.preventDefault();
          onClickHMRRow();
        }}
      >
        <div className="hmr-month-card__title-row">
          <h3 className="hmr-month-card__period">
            {periodLabel}
          </h3>
          {conciliacionTo && (
            <Link
              className="hmr-month-card__bank-link"
              to={conciliacionTo}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                onRequestClose?.();
              }}
            >
              {t("reconciliation.goToBankReconciliation")}
              <Icon name="angle right" />
            </Link>
          )}
        </div>

        <div className="hmr-month-card__progress-block">
          <Progress
            className="hmr-progress-bar"
            percent={reconciledTransactionsProgress}
            progress
            success={unReconciledTransactionsCount === 0}
          >
          </Progress>
          {
            unReconciledTransactionsCount === 0
              ?
              <p className="hmr-month-card__status hmr-month-card__status--success">{t("reconciliation.reconciled")}</p>
              :
              <p className="hmr-month-card__status">
                {t("reconciliation.pendingMovements", { count: unReconciledTransactionsCount })}
              </p>
          }

        </div>
      </div>

      {
        contentVisibility &&
        <div className="hmr-month-card__detail">

          <div>
            {
              hmr.banks.map(bank => {

                const key = bank.bankId + bank.accountNumber + bank.currency;

                return (
                  <div key={key} className="d-flex flex-row py-3 border-bottom">

                    {/* BANK INFO COLUMN */}
                    <div
                      className="d-flex align-items-center"
                      style={{ width: `${banksTableWidths.bankInfo}%` }}
                    >
                      <p className="m-0 fw-semibold">
                        {`${banksFormat[bank.bankId]} - ${t("reconciliation.account")} ${bank.accountNumber}`}
                      </p>
                    </div>

                    {/* BANK CURRENCY COLUMN */}
                    <div
                      className="d-flex flex-column align-items-center"
                      style={{ width: `${banksTableWidths.bankCurrency}%` }}
                    >
                      <small className="m-0">
                        {t("reconciliation.currency")}
                      </small>
                      <small className="m-0">
                        {bank.currency}
                      </small>
                    </div>

                    {/* BANK RECONCILED TRANSACTIONS COLUMN */}
                    <div
                      className="d-flex flex-column align-items-center"
                      style={{ width: `${banksTableWidths.bankReconciledTransactions}%` }}
                    >
                      <small className="m-0">
                        {t("reconciliation.reconciledMovements")}
                      </small>
                      <small className="m-0">
                        {`${bank.reconciledTransactionsCount}/${bank.transactionsCount}`}
                      </small>
                    </div>

                    {/* BANK TOTAL RECEIVED COLUMN */}
                    <div
                      className="d-flex flex-column align-items-center"
                      style={{ width: `${banksTableWidths.bankTotalReceived}%` }}
                    >
                      <small className="m-0">
                        {t("reconciliation.totalCredits")}
                      </small>
                      <small className="m-0">
                        {formatMoneyWithCurrency(bank.receivedTransactionsTotal, bank.currency)}
                      </small>
                    </div>

                    {/* BANK TOTAL SPEND COLUMN */}
                    <div
                      className="d-flex flex-column align-items-center"
                      style={{ width: `${banksTableWidths.bankTotalSpend}%` }}
                    >
                      <small className="m-0">
                        {t("reconciliation.totalDebits")}
                      </small>
                      <small className="m-0">
                        {formatMoneyWithCurrency(bank.spendTransactionsTotal, bank.currency)}
                      </small>
                    </div>
                  </div>
                )
              })
            }
          </div>

          <div>
            {
              hmr.currenciesSummary.map(cs => {
                return (
                  <div key={cs.currency} className="d-flex flex-row py-3">

                    {/* CS INFO COLUMN */}
                    <div
                      className="d-flex"
                      style={{ width: `${currenciesSummaryTableWidths.csInfo}%` }}
                    >
                      <p className="m-0 fw-bold" style={{ fontSize: '9pt' }}>
                        {t("reconciliation.summary", { currency: cs.currency })}
                      </p>
                    </div>

                    {/* CS RECONCILED TRANSACTIONS COLUMN */}
                    <div
                      className="d-flex justify-content-center"
                      style={{ width: `${currenciesSummaryTableWidths.csReconciledTransactions}%` }}
                    >
                      <p className="m-0" style={{ fontSize: '9pt' }}>
                        {`${t("reconciliation.reconciledMovements")}: ${cs.reconciledTransactionsCount}/${cs.transactionsCount}`}
                      </p>
                    </div>

                    {/* CS TOTAL RECEIVED COLUMN */}
                    <div
                      className="d-flex justify-content-center"
                      style={{ width: `${currenciesSummaryTableWidths.csTotalReceived}%` }}
                    >
                      <p className="m-0" style={{ fontSize: '9pt' }}>
                        {`${t("reconciliation.totalCredits")}: ${formatMoneyWithCurrency(cs.receivedTransactionsTotal, cs.currency)}`}
                      </p>
                    </div>

                    {/* CS TOTAL SPEND COLUMN */}
                    <div
                      className="d-flex justify-content-center"
                      style={{ width: `${currenciesSummaryTableWidths.csTotalSpend}%` }}
                    >
                      <p className="m-0" style={{ fontSize: '9pt' }}>
                        {`${t("reconciliation.totalDebits")}: ${formatMoneyWithCurrency(cs.spendTransactionsTotal, cs.currency)}`}
                      </p>
                    </div>
                  </div>
                )
              })
            }
          </div>
        </div>
      }
    </div>
  )
}
